import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FlaskConical,
  Microscope,
  Ruler,
  LineChart,
  Users2,
  Sparkles,
  CheckCircle2,
  Beaker,
  Target,
  Wrench,
  ArrowRight,
  Atom,
  Dna,
  Sigma,
  ExternalLink,
} from "lucide-react";
import { PageHero, CTASection } from "@/components/Bits";
import { FloatingQuote } from "@/components/FloatingQuote";
import { SectionLabel } from "@/components/SectionLabel";
import { Reveal } from "@/components/Reveal";
import { YouTubeEmbed } from "@/components/YouTubeEmbed";

// School Concept Lab photographs (from client's uploaded assets)
const CL_PHOTOS = {
  wide1: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/pxzaux2x_Screenshot%202026-01-31%20153409.png",
  wide2: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/mshg4mb2_Screenshot%202026-01-31%20155637.png",
  wide3: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/25b7tp2q_Screenshot%202026-01-31%20155616.png",
  wide4: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/2cztet2t_Screenshot%202026-01-31%20155353.png",
  wide5: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/bkfdzytl_Screenshot%202026-01-31%20155607.png",
  wide6: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/epezputp_Screenshot%202026-01-31%20153333.png",
  wide7: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/xby25s6p_Screenshot%202026-01-31%20155446.png",
  wide8: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/1c95l3ye_Screenshot%202026-01-31%20155438.png",
  wide9: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/01jhr3yl_Screenshot%202026-01-31%20155428.png",
  wide10: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/b8ikaiad_Screenshot%202026-01-31%20155411.png",
  wide11: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/8z3l5rxz_Screenshot%202026-01-31%20155353.png",
  wide12: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/yejkgzg8_Screenshot%202026-01-31%20154955.png",
  wide13: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/d101ugah_Screenshot%202026-01-31%20154926.png",
  wide14: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/kpnyx3tb_Screenshot%202026-01-31%20153618.png",
  wide15: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/p65mvwul_Screenshot%202026-01-31%20153548.png",
  wide16: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/yaxenvhb_Screenshot%202026-01-31%20153302.png",
  wide17: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/obfejetx_Screenshot%202026-01-31%20150836.png",
  wide18: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/5fsg5d0n_Screenshot%202026-01-31%20150738.png",
  wide19: "https://customer-assets-rejwkqb3.emergentagent.net/job_creator-stem-schools/artifacts/joima38x_Screenshot%202026-01-31%20155512.png",
};

const VIDEOS = [
  { id: "skjsIZeU6oI", label: "Concept Lab · Data analysis in a live experiment" },
  { id: "orJf__OTtPs", label: "Every student engaged at the experiment table" },
  { id: "dsZVIzAjeBM", label: "Chemistry — observing, measuring, verifying" },
  { id: "NJmBpsR4dg4", label: "Concept Lab · Physics investigation" },
  { id: "k0EviptnLrs", label: "Mathematics as a laboratory subject" },
  { id: "J5oJxbneS0o", label: "From experiment to problem-solving" },
];

const WHAT_HAPPENS = [
  { icon: Sparkles,       t: "Teachers bring concepts to life through real demonstrations." },
  { icon: Wrench,         t: "Students handle scientific apparatus and perform experiments." },
  { icon: Microscope,     t: "Every learner observes, measures and records data." },
  { icon: LineChart,      t: "Students analyse results and discuss their observations." },
  { icon: CheckCircle2,   t: "Each concept is investigated and verified through evidence." },
  { icon: Target,         t: "Developed concepts are applied to problems and real-life situations." },
];

const APPARATUS_ENABLES = [
  "Observe scientific phenomena directly",
  "Collect reliable measurements",
  "Record and represent data",
  "Identify relationships and patterns",
  "Analyse graphs and results",
  "Compare predictions with observations",
  "Verify scientific and mathematical concepts",
  "Connect classroom learning with the real world",
];

const EXPERIMENT_STEPS = [
  "Use the required apparatus",
  "Perform the experiment themselves",
  "Observe the phenomenon carefully",
  "Measure and record data",
  "Analyse results and graphs",
  "Discuss their findings",
  "Verify the concept through evidence",
  "Apply the developed concept in problem-solving",
];

const STUDENT_ACTIONS = [
  "Handle apparatus",
  "Perform experiments",
  "Make observations",
  "Record measurements",
  "Analyse evidence",
  "Ask questions",
  "Discuss findings",
  "Verify concepts",
  "Apply learning",
];

const CENTRE_PROCESS = [
  { t: "Apparatus",       d: "Make concepts visible and measurable.",     icon: FlaskConical, hex: "#F0A831" },
  { t: "Demonstrations",  d: "Introduce concepts in action.",              icon: Sparkles,     hex: "#2E6BB0" },
  { t: "Experiments",     d: "Let students investigate independently.",    icon: Beaker,       hex: "#1E9E8A" },
  { t: "Measurements",    d: "Produce evidence.",                          icon: Ruler,        hex: "#E14C7A" },
  { t: "Analysis",        d: "Develops scientific thinking.",              icon: LineChart,    hex: "#8B5CF6" },
  { t: "Verification",    d: "Confirms understanding.",                    icon: CheckCircle2, hex: "#F0A831" },
  { t: "Problems",        d: "Opportunities to apply concepts.",           icon: Target,       hex: "#2E6BB0" },
  { t: "Feedback",        d: "Strengthens learning and mastery.",          icon: Users2,       hex: "#1E9E8A" },
];

export default function ConceptLabs() {
  return (
    <div className="bg-[#050B14] text-onink" data-testid="concept-labs-page">
      <FloatingQuote />

      {/* Sacred-place quote — standalone flashing box */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-40" data-testid="cl-sacred-quote">
        <div className="dk-panel quote-banner rounded-3xl border-2 px-8 md:px-14 py-8 md:py-10 relative overflow-hidden" style={{ borderColor: "rgba(240,161,60,0.45)" }}>
          <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: "#2AA7D4" }} />
          <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full blur-3xl opacity-20" style={{ background: "#F0A13C" }} />
          <div className="relative flex flex-col items-center gap-4">
            <span className="text-c-amber text-2xl leading-none animate-pulse">✦</span>
            <p className="quote-shimmer font-display italic font-medium text-lg sm:text-xl md:text-3xl leading-snug text-center tracking-tight max-w-5xl">
              “Just as temples, churches, mosques, and synagogues are sacred places for worship, laboratories are the sacred places where science and mathematics are truly learned, explored, and discovered.”
            </p>
            <span className="font-mono-label text-[10px] tracking-label uppercase dk-text-muted mt-1">— THECREATOR Education</span>
          </div>
        </div>
      </div>

      <PageHero
        compact
        n="F"
        label="Concept Labs"
        lines={["Concept Labs — The Heart of the", "PBL-TDLE Framework."]}
        intro="Every classroom. Every concept. Every student. A Concept Lab is where every learner investigates, measures, discusses and verifies the concepts that make problem-solving possible."
      />

      {/* Hero video + editorial pull */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6">
            <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Foundational Belief</div>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-4 tracking-tight leading-tight font-light">
              Science and mathematics are built <span className="text-accent">upon concepts</span>.
            </h2>
            <p className="text-white/75 leading-relaxed mt-6">
              These concepts are the ideas, principles and laws that explain how the natural and mathematical world works. Students need them not merely to recall information, but to <span className="text-white">apply knowledge in problem-solving, research, innovation and real-life situations</span>.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              A student cannot solve a complex problem without correctly identifying, connecting and applying multiple concepts. But knowing the definition of a concept does not mean the concept has been developed. Genuine concept development requires students to <span className="text-white">observe, investigate, measure, analyse, discuss and verify</span> it through experience.
            </p>
            <p className="text-white/80 leading-relaxed mt-4">
              This is why concepts and Concept Labs are placed at the <span className="text-accent">centre of THECREATOR PBL-TDLE framework</span>.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black" data-testid="cl-hero-video">
              <YouTubeEmbed id={VIDEOS[0].id} title={VIDEOS[0].label} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* What is a Concept Lab */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="cl-what">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="A">What Is a Concept Lab?</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            A classroom designed for demonstrations, experiments, investigation, measurement, data analysis, discussion and problem-solving.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            It transforms the traditional classroom from a place where students mainly listen into an <span className="text-white">active learning environment</span> where every student learns by doing.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {WHAT_HAPPENS.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={i} delay={(i % 3) * 0.06} className="rounded-xl border border-white/10 p-6 bg-[#050B14] flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center border border-accent/40 bg-accent/10 shrink-0">
                    <Icon className="w-5 h-5 text-accent" />
                  </div>
                  <p className="text-white/80 leading-relaxed">{w.t}</p>
                </Reveal>
              );
            })}
          </div>

          <p className="text-white/70 leading-relaxed mt-10 max-w-3xl italic">
            The objective is not simply to complete an experiment. <span className="not-italic text-white">Every experiment has a defined purpose</span> — to develop, investigate and verify a specific concept.
          </p>
        </div>
      </section>

      {/* Photo mosaic — the labs in action */}
      <section className="border-t border-white/10" data-testid="cl-mosaic">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              CL_PHOTOS.wide1, CL_PHOTOS.wide12, CL_PHOTOS.wide7, CL_PHOTOS.wide2,
              CL_PHOTOS.wide9, CL_PHOTOS.wide13, CL_PHOTOS.wide15, CL_PHOTOS.wide16,
            ].map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 0.05} className="relative aspect-[4/3] overflow-hidden rounded-lg border border-white/10 group">
                <motion.img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Apparatus */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="cl-apparatus">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6 order-2 md:order-1">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black">
              <YouTubeEmbed id={VIDEOS[2].id} title={VIDEOS[2].label} />
            </div>
          </Reveal>
          <Reveal className="md:col-span-6 order-1 md:order-2">
            <SectionLabel n="B">Global-Standard Apparatus</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Apparatus that bring concepts to life.
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              THECREATOR Concept Labs are equipped with <span className="text-white">several hundred global-standard scientific apparatus</span>, including advanced sensors and digital instruments. These tools let students measure accurately, observe clearly and analyse phenomena using reliable data.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              Apparatus convert abstract ideas into visible and measurable experiences: a force can be measured, motion can be graphed, temperature can be monitored, chemical changes can be analysed, biological structures can be observed, and mathematical relationships can be physically explored.
            </p>
            <div className="mt-8 rounded-xl border border-white/10 bg-[#050B14] p-6">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">Accurate apparatus enables students to</div>
              <ul className="grid sm:grid-cols-2 gap-2 mt-4">
                {APPARATUS_ENABLES.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-white/75 text-sm leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-white/60 leading-relaxed mt-6 italic">This makes learning clearer, easier and more meaningful.</p>
          </Reveal>
        </div>

        {/* 1500+ Apparatus panel */}
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20 md:pb-28">
          <Reveal>
            <div className="rounded-3xl border dk-border dk-panel overflow-hidden" data-testid="cl-apparatus-panel">
              {/* Top — big number + subjects */}
              <div className="grid lg:grid-cols-12">
                <div className="lg:col-span-4 p-8 md:p-12 border-b lg:border-b-0 lg:border-r dk-border">
                  <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">The Apparatus Collection</div>
                  <div className="font-display text-[6rem] md:text-[8rem] leading-none dk-text mt-4">1500<span className="text-c-amber">+</span></div>
                  <p className="font-mono-label text-[11px] tracking-label uppercase dk-text-muted mt-2">Global-standard scientific apparatus</p>
                  <p className="dk-text-soft leading-relaxed mt-6 text-sm">
                    Including advanced <a href="https://www.vernier.com" target="_blank" rel="noopener noreferrer" className="text-c-amber hover:underline inline-flex items-center gap-1" data-testid="vernier-link">Vernier<ExternalLink className="w-3.5 h-3.5" /></a> sensors and digital instruments, sourced from Vernier and many other international suppliers — the same equipment used in leading laboratories worldwide.
                  </p>
                </div>
                <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4">
                  {[
                    { icon: Atom, t: "Physics", d: "Motion, force, energy, electricity, magnetism, optics — measured with digital sensors and graphed live.", hex: "#6FA8E8" },
                    { icon: FlaskConical, t: "Chemistry", d: "Reactions, titrations, pH, temperature and gas analysis — observed and quantified precisely.", hex: "#F0A13C" },
                    { icon: Dna, t: "Biology", d: "Microscopy, physiology, ecology and biochemistry — structures and processes made visible.", hex: "#38C9C9" },
                    { icon: Sigma, t: "Mathematics", d: "Geometry, algebra, probability and calculus — explored physically as a laboratory subject.", hex: "#B79CF5" },
                  ].map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <Reveal key={s.t} delay={i * 0.06} className={`p-6 md:p-8 dk-border ${i < 2 ? "border-b lg:border-b-0" : ""} ${i % 2 === 0 ? "border-r lg:border-r" : "lg:border-r"} ${i === 3 ? "lg:border-r-0" : ""}`} data-testid={`apparatus-subject-${s.t.toLowerCase()}`}>
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center border" style={{ borderColor: s.hex, backgroundColor: `${s.hex}22` }}>
                          <Icon className="w-5 h-5" style={{ color: s.hex }} />
                        </div>
                        <div className="font-display text-xl dk-text mt-5 tracking-tight">{s.t}</div>
                        <p className="dk-text-muted text-sm leading-relaxed mt-3">{s.d}</p>
                      </Reveal>
                    );
                  })}
                </div>
              </div>

              {/* Bottom — Concept → Apparatus → Experiment → Problem chain */}
              <div className="border-t dk-border p-8 md:p-12" data-testid="cl-concept-chain">
                <div className="font-mono-label text-[11px] tracking-label uppercase text-c-amber">How Every Apparatus Is Used</div>
                <p className="dk-text-soft leading-relaxed mt-4 max-w-3xl">
                  In the PBL-TDLE framework, <span className="dk-text font-medium">every concept is linked with an apparatus, an experiment and a problem</span> — experiments to <span className="dk-text">develop</span> the concept, and problems to <span className="dk-text">apply</span> it.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                  {[
                    { n: "01", t: "Concept", d: "The idea, law or principle to be developed." },
                    { n: "02", t: "Apparatus", d: "The global-standard instrument that makes it measurable." },
                    { n: "03", t: "Experiment", d: "The investigation that develops the concept through evidence." },
                    { n: "04", t: "Problem", d: "The application that turns the concept into mastery." },
                  ].map((c, i) => (
                    <Reveal key={c.t} delay={i * 0.06} className="relative rounded-2xl border dk-border dk-card p-6">
                      <div className="flex items-center justify-between">
                        <span className="font-mono-label text-[10px] tracking-label uppercase text-c-amber">{c.n}</span>
                        {i < 3 && <ArrowRight className="w-4 h-4 dk-text-muted hidden lg:block" />}
                      </div>
                      <div className="font-display text-xl dk-text mt-3 tracking-tight">{c.t}</div>
                      <p className="dk-text-muted text-sm leading-relaxed mt-2">{c.d}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Thousands of experiments */}
      <section className="border-t border-white/10" data-testid="cl-experiments">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="C">Several Thousand Experiments</SectionLabel>
          <div className="grid md:grid-cols-12 gap-10 items-start mt-6">
            <div className="md:col-span-7">
              <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight font-light">
                Every experiment linked to a specific concept.
              </h2>
              <p className="text-white/70 leading-relaxed mt-6">
                Several thousand experiments have been designed across <span className="text-white">Physics, Chemistry, Biology and Mathematics</span> to investigate and verify concepts from the early years through Grade 12 and A Levels. Students progress from simple observation and measurement to advanced investigation, analysis and interpretation.
              </p>
              <p className="text-white/70 leading-relaxed mt-4">
                During an experiment, students do not passively watch. They:
              </p>
              <ol className="mt-5 space-y-2">
                {EXPERIMENT_STEPS.map((s, i) => (
                  <Reveal key={s} delay={i * 0.03} className="flex items-start gap-3 border border-white/10 rounded-lg p-3 bg-[#070d18]">
                    <span className="font-mono-label text-[10px] tracking-label uppercase text-accent shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-white/80 leading-relaxed">{s}</span>
                  </Reveal>
                ))}
              </ol>
              <p className="text-white/60 leading-relaxed mt-8 italic max-w-2xl">
                This systematic process moves students from <span className="not-italic text-white">concept awareness</span> to <span className="not-italic text-white">concept development</span> and finally to <span className="not-italic text-white">concept mastery</span>.
              </p>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-3">
              {[CL_PHOTOS.wide3, CL_PHOTOS.wide8, CL_PHOTOS.wide10, CL_PHOTOS.wide11].map((src, i) => (
                <Reveal key={i} delay={i * 0.06} className="aspect-square overflow-hidden rounded-lg border border-white/10">
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Every student investigates every concept */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="cl-every-student">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-6">
            <SectionLabel n="D">Every Student · Every Concept</SectionLabel>
            <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight font-light">
              Personal experimentation, not passive observation.
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              A demonstration can introduce a concept, but personal experimentation allows each student to <span className="text-white">investigate and verify it independently</span>. This individual involvement is essential — learning cannot be assumed merely because a teacher has explained something.
            </p>
            <div className="mt-8 rounded-2xl border-l-4 border-accent border-t border-r border-b border-white/10 bg-[#050B14] p-6 md:p-7">
              <div className="font-mono-label text-[11px] tracking-label uppercase text-accent">PBL-TDLE Goal</div>
              <p className="font-display text-2xl md:text-3xl text-white mt-3 tracking-tight leading-snug">
                Every student. <span className="text-accent">Every concept.</span> Concept mastery.
              </p>
              <p className="text-white/70 leading-relaxed mt-4 text-sm">
                Every learner handles tools, performs experiments, collects data, analyses results, participates in discussions and receives feedback. Students become <span className="text-white">active participants</span> in the learning process rather than passive recipients of information.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black">
              <YouTubeEmbed id={VIDEOS[1].id} title={VIDEOS[1].label} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 100% engagement */}
      <section className="border-t border-white/10" data-testid="cl-engagement">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="E">From 100% Engagement to Deeper Learning</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            Real engagement is a <span className="text-accent">purpose</span>, a <span className="text-accent">role</span> and a <span className="text-accent">responsibility</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Student engagement is not created only by making a lesson entertaining. Real engagement happens when every learner has a job to do — and can be seen doing it.
          </p>

          <div className="mt-12 grid grid-cols-3 md:grid-cols-9 gap-3">
            {STUDENT_ACTIONS.map((a, i) => (
              <Reveal
                key={a}
                delay={(i % 9) * 0.04}
                className="aspect-square rounded-lg border border-white/10 bg-[#070d18] p-3 flex flex-col items-center justify-center text-center hover:border-accent/60 transition-colors"
              >
                <span className="font-mono-label text-[10px] tracking-label uppercase text-accent">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white text-sm md:text-base font-display leading-tight mt-2">{a}</span>
              </Reveal>
            ))}
          </div>

          <p className="text-white/70 leading-relaxed mt-10 max-w-3xl">
            This approach is designed to achieve <span className="text-white">maximum participation</span> and ensure that no student is left behind. It also enables teachers to observe what each learner is doing, identify difficulties and provide immediate support.
          </p>
          <p className="text-white/60 leading-relaxed mt-4 max-w-3xl">
            Students learn with greater interest because they are discovering relationships for themselves. Concepts become easier to understand, remain in memory longer and can be applied with greater confidence.
          </p>
        </div>
      </section>

      {/* Video reel */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="cl-video-reel">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="F">The Concept Labs in Motion</SectionLabel>
          <h2 className="font-display text-3xl md:text-4xl text-white mt-6 tracking-tight leading-tight max-w-3xl font-light">
            Watch students learn by <span className="text-accent">doing</span>.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {VIDEOS.slice(3).map((v, i) => (
              <Reveal key={v.id} delay={(i % 3) * 0.08} className="rounded-2xl overflow-hidden border border-white/10 bg-black">
                <div className="aspect-video">
                  <YouTubeEmbed id={v.id} title={v.label} />
                </div>
                <div className="p-4">
                  <div className="font-mono-label text-[10px] tracking-label uppercase text-accent">Reel · {String(i + 1).padStart(2, "0")}</div>
                  <p className="text-white/80 text-sm mt-2 leading-snug">{v.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Concept Labs are at the centre */}
      <section className="border-t border-white/10 bg-[#070d18]" data-testid="cl-centre">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionLabel n="G">Why Concept Labs Are at the Centre</SectionLabel>
          <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight max-w-4xl font-light">
            PBL-TDLE = Problem-Based Learning through Teaching by <span className="text-accent">Demonstrations</span> and Learning by <span className="text-accent">Experiments</span>.
          </h2>
          <p className="text-white/70 leading-relaxed mt-6 max-w-3xl">
            Its purpose is to connect concept development with concept application. At the centre are the concepts students must understand and apply — around them, the processes that make mastery possible.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-14">
            {CENTRE_PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal
                  key={p.t}
                  delay={(i % 4) * 0.06}
                  className="relative border border-white/10 rounded-2xl p-6 bg-[#050B14] hover:border-accent/60 transition-colors overflow-hidden group"
                >
                  <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-30 group-hover:opacity-60 transition-opacity" style={{ backgroundColor: p.hex }} />
                  <div className="relative">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center border" style={{ borderColor: p.hex, backgroundColor: `${p.hex}22` }}>
                      <Icon className="w-5 h-5" style={{ color: p.hex }} />
                    </div>
                    <div className="font-display text-xl text-white mt-4 tracking-tight">{p.t}</div>
                    <p className="text-white/60 text-sm mt-2 leading-relaxed">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <p className="text-white/70 leading-relaxed mt-14 max-w-3xl">
            Concept Labs provide the physical and academic environment in which this entire process takes place. They are <span className="text-white">not an additional facility</span> attached to the curriculum — they are the learning environment required to implement the PBL-TDLE framework effectively.
          </p>
        </div>
      </section>

      {/* From mastery to innovation — closing */}
      <section className="border-t border-white/10" data-testid="cl-innovation">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-7">
            <SectionLabel n="✦">From Concept Mastery to Innovation</SectionLabel>
            <h2 className="font-display text-3xl md:text-5xl text-white mt-6 tracking-tight leading-tight font-light">
              Beyond knowing — to investigate, verify, apply, solve, discover and <span className="text-accent">create</span>.
            </h2>
            <p className="text-white/70 leading-relaxed mt-6">
              Research and innovation begin with strong concepts, careful observation, reliable measurement, scientific investigation and meaningful questions. By experiencing this process from an early age, students gradually learn to think and work like <span className="text-white">scientists, mathematicians, researchers and innovators</span>.
            </p>
            <p className="text-white/70 leading-relaxed mt-4">
              THECREATOR PBL-TDLE framework therefore moves education beyond knowing. Concept Labs develop concepts. Developed concepts make problem-solving possible. Problem-solving prepares students for <span className="text-white">research, innovation and real-world contribution</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/framework" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-5 py-2.5 rounded-full text-sm hover:border-accent hover:text-accent transition-colors" data-testid="cl-cta-framework">
                Explore the full PBL-TDLE Framework <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/enroll" className="inline-flex items-center gap-2 bg-accent text-[#050B14] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-accent/90 transition-colors" data-testid="cl-cta-enroll">
                Enroll <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[CL_PHOTOS.wide4, CL_PHOTOS.wide5, CL_PHOTOS.wide6, CL_PHOTOS.wide19].map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-lg border border-white/10">
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
